import { debounce } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { handleWarningToast } from 'src/components/molecules';
import { TableRow } from 'src/components/moleculesLegacy';
import { UserType, useSearchUsersPaginatedQuery } from 'src/graphql';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { fetcher } from 'src/utils/fetch';
import { SECOND } from 'src/utils/time';

const USER_BY_ID_QUERY = `
  query UserById($id: Int!) {
    userById(id: $id) {
      id
      eid
      email
      name
      displayName
    }
  }
`;

export const useSearchUsers = (
  columns: string[],
  usersToExclude: (number | undefined)[],
  invitedAppUsersOnly?: boolean,
  showOnlyCM?: boolean,
  preselectedUser: UserType | null = null,
  useCaseManagerDirectoryColumns?: boolean,
  suppressCaseManagerAutoAssignToast?: boolean,
  findByUsersIds?: string[],
  enabled = true,
) => {
  const { i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState<UserType | null>(
    preselectedUser,
  );

  const columnHeaders = <TableRow header={true} columns={columns} />;

  let searchBy = [];

  if (invitedAppUsersOnly) {
    searchBy.push({ fieldName: 'status', fieldValue: 'INVITED' });
  }

  if (showOnlyCM) {
    searchBy.push({
      fieldName: 'systemPermissions',
      fieldValue: SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD,
    });
  }

  const users = useSearchUsersPaginatedQuery(
    {
      orderBy: {
        direction: 'asc',
        fieldName: 'name',
      },
      pagination: {
        offset: 0,
        limit: 100,
      },
      searchBy,
      fuzzy: searchTerm.length ? { value: searchTerm } : undefined,
    },
    {
      staleTime: 5 * SECOND,
      enabled,
      keepPreviousData: true,
    },
  );

  const normalizedProvidedIds = useMemo(
    () =>
      Array.from(
        new Set(
          (findByUsersIds ?? [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ),
    [findByUsersIds],
  );

  const providedUsersQuery = useQuery(
    ['SearchUsersByProvidedIds', normalizedProvidedIds],
    async () => {
      const queries = normalizedProvidedIds.map((id) =>
        fetcher<{ userById: UserType | null }, { id: number }>(
          USER_BY_ID_QUERY,
          { id },
        )(),
      );
      const responses = await Promise.all(queries);
      return responses
        .map((response) => response.userById)
        .filter((user): user is UserType => user != null);
    },
    {
      enabled: enabled && normalizedProvidedIds.length > 0,
      staleTime: 5 * SECOND,
    },
  );

  const excludeKey = usersToExclude.join(',');

  const validUsers = useMemo(() => {
    const paginatedUsers =
      users.data?.searchUsersPaginated.items.filter(
        (user) => !usersToExclude.includes(user.id) && user.eid,
      ) || [];
    const providedUsers =
      providedUsersQuery.data?.filter(
        (user) => !usersToExclude.includes(user.id),
      ) || [];
    return Array.from(
      new Map(
        [...paginatedUsers, ...providedUsers].map((user) => [user.id, user]),
      ).values(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.data, providedUsersQuery.data, excludeKey]);

  useEffect(() => {
    if (
      !selectedUser &&
      showOnlyCM &&
      suppressCaseManagerAutoAssignToast !== true
    ) {
      handleWarningToast({
        toastMessage: 'A case manager will be automatically assigned.',
        autoClose: 2000,
      });
    }
  }, [showOnlyCM, selectedUser, suppressCaseManagerAutoAssignToast]);

  const sortedValidUsers = useMemo(
    () =>
      [...validUsers].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', i18n.language),
      ),
    [validUsers, i18n.language],
  );

  const rows = sortedValidUsers.map((user) => (
    <TableRow
      testId={`search-bar-row/${user.eid || user.id}`}
      key={user.eid || user.id}
      selected={user.eid === selectedUser?.eid}
      onRowClick={() => {
        if (user.eid === selectedUser?.eid) {
          setSelectedUser(null);
        } else {
          setSelectedUser(user as UserType);
        }
      }}
      columns={
        useCaseManagerDirectoryColumns
          ? [
              user.name || 'Anonymous',
              user.department || '—',
              user.email || '—',
            ]
          : [
              user.name || 'Anonymous',
              user.role || 'No specified role',
              user.department || 'No specified department',
              user.country || 'N/A',
            ]
      }
      rowId={String(user.eid || user.id)}
    />
  ));

  const debouncedChangeHandler = React.useMemo(() => {
    return debounce((value) => setSearchTerm(value), 300);
  }, [setSearchTerm]);

  React.useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  return {
    selectedUser,
    columnHeaders,
    rows,
    searchUsers: debouncedChangeHandler,
    setSelectedUser,
    sortedValidUsers,
  };
};
